import { t, isArabic } from "../i18n";
import { designPricing } from "../data/pricingData.js";

/*
 * "6 LM L Shape Kitchen With Bar Handle" cannot be swapped word for word.
 * Arabic leads with the noun, so the kitchen comes first and the size, shape
 * and handle follow it.
 */
function kitchenSubtitle(size, shape, handle) {
  return isArabic()
    ? [t("Kitchen"), shape, size, handle].filter(Boolean).join(" ")
    : `${size} ${shape} Kitchen With ${handle}`;
}

const SHAPE_INFO = {
  "l-shape": { title: t("L-SHAPE KITCHEN"), subtitle: t("L Shape") },
  galley: { title: t("GALLEY KITCHEN"), subtitle: t("Galley") },
  "u-shape": { title: t("U-SHAPE KITCHEN"), subtitle: t("U Shape") },
  "1-wall": { title: t("1 WALL KITCHEN"), subtitle: t("Wall") },
};

function SpecRow({ label, value }) {
  return (
    <>
      <span className="font-bold text-gray-900 text-xs whitespace-nowrap leading-snug">
        {label}:
      </span>
      <span className="text-gray-700 text-xs leading-snug">{value}</span>
    </>
  );
}

function valueOrDash(value) {
  return value === undefined || value === null || value === "" ? "-" : value;
}

function withUnit(value, unit) {
  if (value === undefined || value === null || value === "") return "-";
  const text = String(value).trim();
  if (!text) return "-";
  return text.toLowerCase().includes(unit.toLowerCase()) ? text : `${text} ${unit}`;
}

function termById(terms = [], id) {
  const numericId = Number(id);
  return terms.find((term) => Number(term.id) === numericId) ?? null;
}

function dynamicShapeInfo(shapeTerm) {
  const shapeData = SHAPE_INFO[shapeTerm?.slug] ?? null;
  if (shapeData) return shapeData;

  const name = shapeTerm?.name || t("Kitchen");
  return {
    title: `${name} Kitchen`.toUpperCase(),
    subtitle: name,
  };
}

function dynamicHandleName(info, handleTerm, properties) {
  if (properties.handle_type) return properties.handle_type;
  if (info.handle === "with") return t("Bar Handle");
  if (info.handle === "none") return t("Integrated Handle");
  return handleTerm?.name || t("Selected Handle");
}

function plainText(value) {
  return String(value ?? "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function modalDataFromInfo(info) {
  const { shapeId, handle, design } = info;

  if (info.dynamic) {
    const config = info.productConfig;
    const product = config?.product ?? {};
    const properties = config?.properties ?? {};
    const measurements = config?.measurements ?? {};
    const shapeTerm = termById(config?.shapes, properties.shape_id);
    const handleTerm = termById(config?.handles, properties.handle_id);
    const { title: shapeTitle, subtitle: shapeSubtitle } =
      dynamicShapeInfo(shapeTerm);
    const handleName = dynamicHandleName(info, handleTerm, properties);
    const totalSize =
      properties.total_kitchen_size ||
      measurements.total_size ||
      properties.kitchen_size ||
      design?.size;
    const carcassDepth =
      properties.carcass_depth || measurements.carcass_depth || "600";
    // Mirrors the static app's `woodUnits.group1.wall > 0`; the measurement
    // lengths are not captured in the admin form so they are always zero.
    const hasWallUnit = Number(config?.price_matrix?.wall_unit?.g1 || 0) > 0;
    const description = plainText(product.description);

    return {
      shapeTitle,
      subtitle: kitchenSubtitle(withUnit(totalSize, t("LM")), shapeSubtitle, handleName),
      articleNo: valueOrDash(
        product.article_number || design?.id || product.sku || product.id,
      ),
      countertopMat: valueOrDash(
        properties.countertop_material ||
          (info.handle === "with" ? t("Acrylic Solid Surface") : t("Solid Surface")),
      ),
      woodMaterial: valueOrDash(
        properties.wood_material || t(t("Laminate Bonded Chipboards")),
      ),
      skirtingMaterial: valueOrDash(properties.skirting_material || t(t("PVC"))),
      glassFrame: valueOrDash(
        properties.glass_frame || (hasWallUnit ? t("Aluminum Frame") : "-"),
      ),
      totalKitchenSize: withUnit(totalSize, t("LM")),
      carcassDepth: withUnit(carcassDepth, "mm"),
      drawerRail: valueOrDash(
        properties.drawer_rail || t(t("Hafele - Soft Closing")),
      ),
      hinges: valueOrDash(properties.hinges || t(t("Bergo - Soft Closing"))),
      handleName: valueOrDash(properties.handle_type || handleName),
      description:
        description || t("Kitchen appliances are displayed for illustration purposes. It is excluded from the price. Price includes delivery and installation."),
    };
  }

  const { title: shapeTitle, subtitle: shapeSubtitle } = SHAPE_INFO[shapeId] ?? {};
  const articleNo = handle === "none" ? design.id.replace(/TB$/, "GL") : design.id;
  const handleName = handle === "with" ? t("Bar Handle") : t("Integrated Handle");
  const countertopMat = handle === "with" ? t("Acrylic Solid Surface") : t("Solid Surface");
  const pricing = designPricing[design.id];
  const hasWallUnit = pricing ? pricing.woodUnits["group1"].wall > 0 : true;

  return {
    shapeTitle,
    subtitle: `${design.size} LM ${shapeSubtitle} Kitchen With ${handleName}`,
    articleNo,
    countertopMat,
    woodMaterial: t("Laminate Bonded Chipboards"),
    skirtingMaterial: t("PVC"),
    glassFrame: hasWallUnit ? t("Aluminum Frame") : "-",
    totalKitchenSize: `${design.size} LM`,
    carcassDepth: "600 mm",
    drawerRail: t("Hafele - Soft Closing"),
    hinges: t("Bergo - Soft Closing"),
    handleName,
    description: t("Kitchen appliances are displayed for illustration purposes. It is excluded from the price. Price includes delivery and installation."),
  };
}

export default function InformationModal({ info, onClose }) {
  if (!info) return null;

  const modal = modalDataFromInfo(info);

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 md:p-6"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-4xl md:rounded-[40px] max-w-5xl w-full p-6 md:p-10 overflow-y-auto max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
          {/* Title + subtitle */}
          <div>
            <h2 className="text-xl sm:text-2xl md:text-3xl font-black text-gray-900 leading-none tracking-tight">
              {modal.shapeTitle}
            </h2>
            <p className="text-xs font-semibold text-gray-800 mt-2">
              {modal.subtitle}
            </p>
          </div>

          {/* Article No */}
          <div className="sm:ml-8 sm:shrink-0">
            <p className="text-[10px] text-gray-400 tracking-widest uppercase mb-1.5">{t("Article No")}</p>
            <div className="bg-gray-600 text-white font-bold text-sm px-6 py-2 text-center min-w-[16ch]">
              {modal.articleNo}
            </div>
          </div>
        </div>

        {/* Divider */}
        <hr className="border-gray-300 mb-5" />

        {/* Specs
             Mobile:  one shared grid — all rows share the same label column width
             Desktop: two side-by-side grids via flex */}
        <div className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-0.5 sm:flex sm:gap-16 mb-6 md:mb-8">
          {/* Group 1 + 2 */}
          <div className="contents sm:grid sm:grid-cols-[max-content_1fr] sm:gap-x-4 sm:gap-y-0.5 sm:content-start sm:self-start">
            <SpecRow label={t("Countertop Material")} value={modal.countertopMat} />
            <SpecRow label={t("Wood Material")} value={modal.woodMaterial} />
            <SpecRow label={t("Skirting Material")} value={modal.skirtingMaterial} />
            <SpecRow label={t("Glass Frame")} value={modal.glassFrame} />
            <div className="col-span-2 h-4" />
            <SpecRow label={t("Total Kitchen Size")} value={modal.totalKitchenSize} />
            <SpecRow label={t("Carcass Depth")} value={modal.carcassDepth} />
          </div>

          {/* Group 3 */}
          <div className="contents sm:grid sm:grid-cols-[max-content_1fr] sm:gap-x-4 sm:gap-y-0.5 sm:content-start sm:self-start">
            <div className="col-span-2 h-4 sm:hidden" />
            <SpecRow label={t("Drawer Rail")} value={modal.drawerRail} />
            <SpecRow label={t("Hinges")} value={modal.hinges} />
            <SpecRow label={t("Handle Type")} value={modal.handleName} />
          </div>
        </div>

        {/* Disclaimer */}
        <p className="text-[11px] md:text-xs text-gray-500 leading-relaxed">
          {modal.description}
        </p>
      </div>
    </div>
  );
}
